import { useRef, useEffect } from 'react'
import { Building2, Globe, MapPin } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { useParallax } from '../hooks/useParallax'
import { useScrollAnimation, useScrollAnimateChildren } from '../hooks/useScrollAnimation'

/* ─── Types ─── */
interface Company {
  name: string
  nameEn?: string
  specialty: string
  specialtyEn?: string
  location?: string
  logo?: string      // path or URL
  initials?: string  // fallback
  color?: string     // initials bg color
}

interface Sector {
  title: string
  titleEn: string
  color: string
  image: string
  companies: Company[]
}


/* ─── Egypt Sectors ─── */
const egyptSectors: Sector[] = [
  {
    title: 'شركات المعادن',
    titleEn: 'Metals Companies',
    color: '#6b7280',
    image: '/logos/metal-companies.jpeg',
    companies: [
      { name: 'الغربلي', nameEn: 'Al-Gharbaly / Gieco', specialty: 'صناعات حديدية وصلب', specialtyEn: 'Steel & Metal Industries', logo: '/logos/al-gharbaly-gieco.png', initials: 'GH' },
      { name: 'السويدي', nameEn: 'Elsewedy Electric', specialty: 'كابلات وأسلاك كهربائية', specialtyEn: 'Cables & Electrical Wires', logo: '/logos/elsewedy.svg', initials: 'EL' },
      { name: 'روابي القابضة', nameEn: 'Rawabi Holding', specialty: 'مجموعة استثمارية متنوعة', specialtyEn: 'Diversified Investment Group', logo: '/logos/rawabi-holding.svg', initials: 'RH' },
      { name: 'أوراسكوم', nameEn: 'Orascom', specialty: 'إنشاءات وصناعات متنوعة', specialtyEn: 'Construction & Diverse Industries', logo: '/logos/orascom.svg', initials: 'OR' },
    ],
  },
  {
    title: 'المواد الغذائية',
    titleEn: 'Food Products',
    color: '#ef4444',
    image: '/logos/food-products.jpeg',
    companies: [
      { name: 'معن عبد الله للمجمدات', nameEn: 'Maan Abdullah Frozen Food', specialty: 'أغذية مجمدة', specialtyEn: 'Frozen Food', initials: 'عم', color: '#ef4444' },
      { name: 'شركة البحار', nameEn: 'Al-Bahar Co.', specialty: 'مواد غذائية', specialtyEn: 'Food Products', initials: 'بح', color: '#dc2626' },
      { name: 'شركة الحوت', nameEn: 'Al-Hout Co.', specialty: 'مواد غذائية', specialtyEn: 'Food Products', initials: 'حت', color: '#b91c1c' },
      { name: 'مصنع الوفاق السعودي', nameEn: 'Al-Wefaq Saudi Factory', specialty: 'منتجات غذائية', specialtyEn: 'Food Products', initials: 'وف', color: '#991b1b' },
      { name: 'سايلو مصر للصناعات الغذائية', nameEn: 'Silo Foods Egypt', specialty: 'صناعات غذائية', specialtyEn: 'Food Industries', initials: 'سل', color: '#ef4444' },
      { name: 'المصرية السعودية', nameEn: 'Egyptian Saudi Co.', specialty: 'مواد غذائية', specialtyEn: 'Food Products', initials: 'مس', color: '#dc2626' },
      { name: 'العبيكان', nameEn: 'Al-Obeikan', specialty: 'تعبئة وتغليف غذائي', specialtyEn: 'Food Packaging', initials: 'عب', color: '#b91c1c' },
      { name: 'مويكا', nameEn: 'Moyca Grapes', specialty: 'تصدير عنب, إسبانيا', specialtyEn: 'Grape Exporter, Spain', initials: 'MG', color: '#991b1b' },
    ],
  },
  {
    title: 'التعبئة والتغليف',
    titleEn: 'Packaging',
    color: '#e87722',
    image: '/logos/packing.jpeg',
    companies: [
      { name: 'الشركة المصرية', nameEn: 'Egyptian Co.', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'مص', color: '#e87722' },
      { name: 'ايكو باك', nameEn: 'Eco Pack', specialty: 'تغليف بيئي', specialtyEn: 'Eco Packaging', logo: '/logos/ecopack.png', initials: 'EP' },
      { name: 'فليكس فيلم', nameEn: 'Flex Film', specialty: 'أفلام تغليف مرنة', specialtyEn: 'Flexible Packaging Films', logo: '/logos/flex-film.png', initials: 'FF' },
      { name: 'هوتاماكي', nameEn: 'Huhtamaki', specialty: 'تغليف صناعي وغذائي', specialtyEn: 'Industrial & Food Packaging', logo: '/logos/huhtamaki.svg', initials: 'HM' },
      { name: 'الفراشة', nameEn: 'Al-Farasha', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', logo: '/logos/al-farasha.jpg', initials: 'فر' },
      { name: 'اي بال', nameEn: 'I-Pack', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', logo: '/logos/i-pack.png', initials: 'IP' },
      { name: 'المصرية الكندية', nameEn: 'Egyptian Canadian Co.', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'مك', color: '#ea580c' },
      { name: 'روتو هاوس', nameEn: 'Roto House', specialty: 'طباعة روتوغرافية', specialtyEn: 'Rotogravure Printing', logo: '/logos/roto-house.png', initials: 'RH' },
      { name: 'فالكون', nameEn: 'Falcon', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', logo: '/logos/falcon.webp', initials: 'FA' },
      { name: 'اليكس باك', nameEn: 'Alex Pack', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'AP', color: '#d97706' },
      { name: 'العالمية فليكس', nameEn: 'Al-Alamiya Flex', specialty: 'أفلام تغليف', specialtyEn: 'Packaging Films', initials: 'عف', color: '#b45309' },
      { name: 'شركة المن', nameEn: 'El-Mon Co.', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'مك', color: '#e87722' },
      { name: 'سايلون روتو باك', nameEn: 'Sylon Roto Pack', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'SP', color: '#ea580c' },
      { name: 'برنت هوم', nameEn: 'Print Home', specialty: 'طباعة ومطبوعات', specialtyEn: 'Printing & Publications', initials: 'PH', color: '#c2410c' },
    ],
  },
  {
    title: 'المواد الكيميائية',
    titleEn: 'Chemicals',
    color: '#16a34a',
    image: '/logos/chemicals.jpg',
    companies: [
      { name: 'شركة هليوبوليس', nameEn: 'Heliopolis Co.', specialty: 'كيماويات صناعية, الهيئة العربية للتصنيع', specialtyEn: 'Industrial Chemicals, Arab Org. for Industrialization', initials: 'هل', color: '#16a34a' },
      { name: 'شركة المنصورة لصناعة الراتنجات', nameEn: 'Mansoura Resins Co.', specialty: 'تصنيع الراتنجات الصناعية', specialtyEn: 'Industrial Resins Manufacturing', initials: 'مر', color: '#15803d' },
      { name: 'شركة تارجيت للمواد الكيماوية', nameEn: 'Target Chemicals', specialty: 'مواد كيماوية متخصصة', specialtyEn: 'Specialty Chemicals', initials: 'TG', color: '#166534' },
      { name: 'شركة سيكا', nameEn: 'Sika', specialty: 'مواد بناء وعوازل ولاصقات', specialtyEn: 'Construction, Sealants & Adhesives', logo: '/logos/sika.svg', initials: 'SK' },
      { name: 'شركة تري كيم', nameEn: 'Tri-Chem', specialty: 'بولي إيثيلين', specialtyEn: 'Polyethylene', initials: 'نك', color: '#16a34a' },
      { name: 'شركة سبيس كيميكال', nameEn: 'Space Chemical', specialty: 'مواد كيماوية متخصصة', specialtyEn: 'Specialty Chemicals', initials: 'SC', color: '#15803d' },
    ],
  },
  {
    title: 'خامات البلاستيك والبولي بروبلين',
    titleEn: 'Plastic & Polypropylene Raw Materials',
    color: '#3b82f6',
    image: '/logos/plastic-images.webp',
    companies: [
      { name: 'الصفوة', nameEn: 'Al-Safwa', specialty: 'خامات بلاستيك تجارية', specialtyEn: 'Commercial Plastic Materials', logo: '/logos/al-safwa.png', initials: 'صف' },
      { name: 'الشروق', nameEn: 'Al-Shorouk', specialty: 'خامات بلاستيك تجارية', specialtyEn: 'Commercial Plastic Materials', initials: 'شر', color: '#3b82f6' },
      { name: 'المصنع العربي لصناعة العبوات', nameEn: 'Arab Factory for Containers', specialty: 'عبوات بلاستيكية', specialtyEn: 'Plastic Containers', initials: 'مع', color: '#2563eb' },
      { name: 'فليكس فيلم', nameEn: 'Flex Film', specialty: 'خامات تغليف مرنة', specialtyEn: 'Flexible Packaging Materials', logo: '/logos/flex-film.png', initials: 'FF' },
      { name: 'الرائد للبلاستيك', nameEn: 'Al-Raed Plastics', specialty: 'منتجات بلاستيكية', specialtyEn: 'Plastic Products', initials: 'رد', color: '#1d4ed8' },
      { name: 'ايكوبال', nameEn: 'Eco Pal', specialty: 'خامات بلاستيك', specialtyEn: 'Plastic Raw Materials', initials: 'EP', color: '#3b82f6' },
      { name: 'المصرية', nameEn: 'Egyptian Co.', specialty: 'خامات بلاستيكية', specialtyEn: 'Plastic Materials', initials: 'مص', color: '#2563eb' },
      { name: 'العالمية للبلاستيك', nameEn: 'International Plastics', specialty: 'خامات بلاستيك', specialtyEn: 'Plastic Materials', location: 'الإمارات', initials: 'عل', color: '#1d4ed8' },
      { name: 'الالكتروم', nameEn: 'Al-Elektrom', specialty: 'منتجات زراعية', specialtyEn: 'Agricultural Products', initials: 'الك', color: '#1e40af' },
    ],
  },
  {
    title: 'المنتجات الورقية الصحية',
    titleEn: 'Hygienic Paper Products',
    color: '#2563a8',
    image: '/logos/Standard-Toilet-Paper-Rolls.webp',
    companies: [
      { name: 'نيو بيبي', nameEn: 'New Baby', specialty: 'حفاضات أطفال - ميتي بيبي', specialtyEn: 'Baby Diapers, Meety Baby', logo: '/logos/new-baby.png', initials: 'NB' },
      { name: 'الدولية انترناشيونال', nameEn: 'International Co.', specialty: 'حفاضات أطفال, هالو بيبي', specialtyEn: 'Baby Diapers, Hello Baby', initials: 'دو', color: '#2563a8' },
      { name: 'الصفا', nameEn: 'Al-Safa', specialty: 'كمامات وبدل وقفازات صحية', specialtyEn: 'Masks, Suits & Hygiene Gloves', initials: 'صف', color: '#1d4ed8' },
      { name: 'المدار', nameEn: 'Al-Madar', specialty: 'مواد سيلوزية, الإسكندرية', specialtyEn: 'Cellulose Materials, Alexandria', initials: 'مد', color: '#1e40af' },
      { name: 'سوفتكس', nameEn: 'Softtex', specialty: 'طبقة الغلاف للحفاضات, موفينيل', specialtyEn: 'Diaper Back Sheet, Movinyl', initials: 'SF', color: '#2563a8' },
      { name: 'شركة البردي', nameEn: 'Al-Bardi', specialty: 'حفاضات أطفال, فاين بيبي', specialtyEn: 'Baby Diapers, Fine Baby', logo: '/logos/fine-baby.svg', initials: 'بر' },
      { name: 'حياة إيجيبت', nameEn: 'Hayat Egypt', specialty: 'حفاضات, مولفكس', specialtyEn: 'Diapers, Molfix', logo: '/logos/hayat-kimya.jpg', initials: 'HE' },
      { name: 'إيجيبت كير', nameEn: 'Egypt Care', specialty: 'حفاضات كبار, المصرية الحديثة', specialtyEn: 'Adult Diapers, Modern Egyptian', initials: 'EC', color: '#1d4ed8' },
      { name: 'نيو شارم', nameEn: 'New Charm', specialty: 'حفاضات أطفال, بيبي جوي', specialtyEn: 'Baby Diapers, Baby Joy', logo: '/logos/baby-joy.png', initials: 'NC' },
    ],
  },
  {
    title: 'الأقمشة غير المنسوجة',
    titleEn: 'Non-Woven Fabrics',
    color: '#8b5cf6',
    image: '/logos/non-woven-fabrics.jpg',
    companies: [
      { name: 'فابريكا', nameEn: 'Fabrica', specialty: 'أقمشة غير منسوجة, الأولى في مصر', specialtyEn: 'Non-Woven Fabrics, First in Egypt', initials: 'FA', color: '#8b5cf6' },
      { name: 'إيجيبت جروب', nameEn: 'Egypt Group', specialty: 'عوازل وتثبيت التربة', specialtyEn: 'Insulation & Soil Stabilization', initials: 'EG', color: '#7c3aed' },
      { name: 'النساجون الشرقيون', nameEn: 'Oriental Weavers', specialty: 'أقمشة صناعية', specialtyEn: 'Industrial Fabrics', logo: '/logos/oriental-weavers.png', initials: 'OW' },
    ],
  },
]

/* ─── Gulf Companies ─── */
interface GulfCompany extends Company {
  region?: string
}

const gulfCompanies: GulfCompany[] = [
  { name: 'الخليج لصناعات التغليف المحدودة', nameEn: 'Gulf Packaging Industries', specialty: 'صناعة التغليف', specialtyEn: 'Packaging Manufacturing', logo: '/logos/gulf-packaging-industries.svg', initials: 'GPI' },
  { name: 'شركة الميثانول للمواد الكيماوية', nameEn: 'Methanol Chemicals Co.', specialty: 'مواد كيماوية', specialtyEn: 'Chemicals', initials: 'MC', color: '#0891b2' },
  { name: 'شركة تصنيع', nameEn: "TASNEE", specialty: 'بتروكيماويات ومعادن', specialtyEn: 'Petrochemicals & Metals', initials: 'TN', color: '#0e7490' },
  { name: 'الشركة السعودية للبوليمرات', nameEn: 'Saudi Polymers Co.', specialty: 'بوليمرات وبتروكيماويات', specialtyEn: 'Polymers & Petrochemicals', initials: 'SP', color: '#0891b2' },
  { name: 'مصنع الحنيني للبلاستيك', nameEn: "Al-Hunaini Plastics Factory", specialty: 'منتجات بلاستيكية', specialtyEn: 'Plastic Products', initials: 'حن', color: '#0e7490' },
  { name: 'شركة إيفونيك', nameEn: 'Evonik', specialty: 'كيماويات متخصصة عالمية', specialtyEn: 'Global Specialty Chemicals', logo: '/logos/evonik.svg', initials: 'EV' },
  { name: 'شركة المتكاملة للتغليف', nameEn: 'Integrated Packaging Industries Co.', specialty: 'حلول تغليف متكاملة', specialtyEn: 'Integrated Packaging Solutions', initials: 'مت', color: '#0891b2' },
  { name: 'الشركة الدولية للمنتجات الورقية الصحية', nameEn: "Al-Dawleya hygienic Paper Products", specialty: 'منتجات ورقية صحية', specialtyEn: 'Hygienic Paper Products', initials: 'دع', color: '#0e7490' },
  { name: 'شركة ارشوت للتعبئة والتغليف', nameEn: 'Arshot Packaging', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging & Filling', initials: 'AR', color: '#0891b2' },
  { name: 'شركة العوجان للمرطبات وحافظات الطعام', nameEn: 'Aujan Industries', specialty: 'مرطبات وحافظات طعام', specialtyEn: 'Soft Drinks & Food Containers', initials: 'عج', color: '#0e7490' },
  { name: 'شركة الفيصل للمواد الغذائية', nameEn: 'Al-Faisal Food Products', specialty: 'مواد غذائية وعبوات', specialtyEn: 'Food Products & Containers', initials: 'فص', color: '#0891b2' },
  { name: 'شركة كرتون العبيكان', nameEn: 'Obeikan Carton', specialty: 'كرتون وعبوات, عصير وبيبسي', specialtyEn: 'Carton & Containers, Juice & Pepsi', initials: 'كع', color: '#0e7490' },
  { name: 'شركة الكحيمي للبولي فيل', nameEn: 'Al-Kuhaimi for Poly-fil', specialty: 'بولي فيل', specialtyEn: 'Poly-fil', initials: 'كم', color: '#0891b2' },
  { name: 'شركة هوتاماكي الإماراتية', nameEn: 'Huhtamaki UAE', specialty: 'روتو تغليف وعبوات', specialtyEn: 'Roto Packaging & Containers', logo: '/logos/huhtamaki.svg', initials: 'HU', location: 'الإمارات', region: 'uae' },
  { name: 'شركة غربال', nameEn: 'Ghazal Co.', specialty: 'تعبئة وتغليف', specialtyEn: 'Packaging', initials: 'غز', color: '#0891b2' },
  { name: 'الدكيب المطبوعة', nameEn: 'Al-Dukayb Prints', specialty: 'مطبوعات', specialtyEn: 'Printing', location: 'مصر', initials: 'دك', color: '#0e7490' },
  { name: 'شركة الرواد', nameEn: 'Al-Ruwad Co.', specialty: 'خامات ورقية ومستلزمات', specialtyEn: 'Paper Materials & Supplies', initials: 'رو', color: '#0891b2' },
  { name: 'شركة ريماس', nameEn: 'Remas Co.', specialty: 'خامات ورقية ومستلزمات طبية وبلاستيك', specialtyEn: 'Paper, Medical Supplies & Plastics', initials: 'RM', color: '#0e7490' },
  { name: 'شركة العبوجة للصادرات', nameEn: 'Al-Abuja Exports', specialty: 'صادرات ومستلزمات طبية', specialtyEn: 'Exports & Medical Supplies', initials: 'عو', color: '#0891b2' },
  { name: 'شركة JBF البحرين', nameEn: 'JBF Bahrain', specialty: 'بولي فيل', specialtyEn: 'Poly-fil', location: 'البحرين', initials: 'JBF', color: '#0e7490', region: 'bahrain' },
  { name: 'شركة ألواح الخليج', nameEn: 'Gulf Panels Co.', specialty: 'ألواح مركبة, تكنوبوند', specialtyEn: 'Composite Panels, Technobond', initials: 'AG', color: '#0891b2' },
  { name: 'شركة الريان', nameEn: 'Al-Rayyan Co.', specialty: 'بلاستيك ومنتجات صحية', specialtyEn: 'Plastics & Hygiene Products', logo: '/logos/al-rayyan.png', initials: 'ري' },
  { name: 'المتمكن للبولي فيل', nameEn: 'Al-Mutakammin for Poly-Fil', specialty: 'بولي فيل', specialtyEn: 'Poly-fil', initials: 'مت', color: '#0e7490' },
  { name: 'الكلثوم', nameEn: 'Al-Kulthum', specialty: 'بولي إيثيلين', specialtyEn: 'Polyethylene', initials: 'كل', color: '#0891b2' },
]

/* ─── Company Card ─── */
function CompanyCard({ company, accentColor }: { company: Company; accentColor: string }) {
  const displayColor = company.color ?? accentColor

  return (
    <div className="co-card">
      <div className="co-card-logo-wrap">
        {company.logo ? (
          <img
            src={company.logo}
            alt={company.name}
            className="co-card-logo"
            loading="lazy"
            onError={(e) => {
              const img = e.target as HTMLImageElement
              img.style.display = 'none'
              const fb = img.nextElementSibling as HTMLElement
              if (fb) fb.style.display = 'flex'
            }}
          />
        ) : null}
        <div
          className="co-card-initials"
          style={{
            background: displayColor,
            display: company.logo ? 'none' : 'flex',
          }}
        >
          {(company.initials ?? company.name).slice(0, 2)}
        </div>
      </div>
      <div className="co-card-info">
        <span className="co-card-name">{company.name}</span>
        <span className="co-card-specialty">{company.specialty}</span>
        {company.location && (
          <span className="co-card-location">
            <MapPin size={11} />
            {company.location}
          </span>
        )}
      </div>
    </div>
  )
}

/* ─── Sector Block ─── */
function SectorBlock({ sector }: { sector: Sector }) {
  const ref = useScrollAnimation('anim-visible', 0.08)
  return (
    <div className="sector-block anim-child" ref={ref as React.RefObject<HTMLDivElement>}>
      <div className="sector-block-header" style={{ borderColor: sector.color }}>
        <div className="sector-block-img-wrap">
          <img src={sector.image} alt={sector.title} className="sector-block-img" loading="lazy" />
          <div className="sector-block-img-overlay" style={{ background: sector.color }} />
        </div>
        <div className="sector-block-title-wrap">
          <div className="sector-block-dot" style={{ background: sector.color }} />
          <h3 className="sector-block-title" style={{ color: sector.color }}>{sector.title}</h3>
          <span className="sector-block-count" style={{ background: sector.color }}>
            {sector.companies.length} {sector.companies.length === 1 ? 'شركة' : 'شركات'}
          </span>
        </div>
      </div>
      <div className="sector-block-grid">
        {sector.companies.map((co, i) => (
          <CompanyCard key={i} company={co} accentColor={sector.color} />
        ))}
      </div>
    </div>
  )
}

/* ─── Main Page ─── */
export default function Clients() {
  const { lang } = useApp()
  const isAr = lang === 'ar'

  const heroRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = heroRef.current
    if (!el) return
    setTimeout(() => el.classList.add('anim-visible'), 80)
  }, [])

  const parallaxRef = useParallax()
  const gulfRef = useScrollAnimation('anim-visible', 0.08)
  const sectorsRef = useScrollAnimateChildren()

  return (
    <main className="page clients-page" dir={isAr ? 'rtl' : 'ltr'}>

      {/* Hero */}
      <section className="page-hero" ref={heroRef as React.RefObject<HTMLElement>}>
        <div className="page-hero-overlay" />
        <img
          ref={parallaxRef}
          src="/assets/images/hero-clients.jpg"
          alt="clients"
          className="page-hero-img parallax-img"
        />
        <div className="page-hero-content">
          <h1>{isAr ? 'عملاؤنا' : 'Our Clients'}</h1>
          <p>{isAr ? 'نفخر بثقة عملائنا داخل مصر وخارجها' : 'We are proud of the trust of our clients inside and outside Egypt'}</p>
        </div>
      </section>

      {/* Gulf / International */}
      <section className="section clients-outside-section">
        <div className="section-inner">
          <div className="clients-section-header">
            <div className="clients-section-badge clients-badge-outside">
              <Globe size={20} />
              <span>{isAr ? 'العملاء الدوليون' : 'International Clients'}</span>
            </div>
            <h2 className="clients-section-title">
              {isAr ? 'منطقة الخليج العربي والعالم' : 'Gulf Region & International'}
            </h2>
            <p className="clients-section-sub">
              {isAr
                ? 'نمتد بخدماتنا اللوجستية إلى أسواق الخليج العربي والعالم'
                : 'We extend our logistics services to Gulf markets and beyond'}
            </p>
          </div>

          <div className="gulf-block anim-fade" ref={gulfRef as React.RefObject<HTMLDivElement>}>
            <div className="gulf-block-hero-wrap">
              <img
                src="/assets/images/hero-articles.jpg"
                alt="Gulf"
                className="gulf-block-hero-img"
                loading="lazy"
              />
              <div className="gulf-block-hero-overlay" />
              <div className="gulf-block-hero-label">
                <Globe size={22} />
                <span>{isAr ? 'منطقة الخليج العربي' : 'Arab Gulf Region'}</span>
              </div>
            </div>
            <div className="gulf-companies-grid">
              {gulfCompanies.map((co, i) => (
                <CompanyCard key={i} company={co} accentColor="#0891b2" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Egypt Sectors */}
      <section className="section clients-inside-section">
        <div className="section-inner">
          <div className="clients-section-header">
            <div className="clients-section-badge clients-badge-inside">
              <Building2 size={20} />
              <span>{isAr ? 'داخل مصر' : 'Inside Egypt'}</span>
            </div>
            <h2 className="clients-section-title">
              {isAr ? 'الشركات المصرية' : 'Egyptian Companies'}
            </h2>
            <p className="clients-section-sub">
              {isAr
                ? 'نخدم كبرى الشركات المصرية عبر قطاعات متعددة'
                : "Serving Egypt's leading companies across multiple sectors"}
            </p>
          </div>

          <div className="sectors-list" ref={sectorsRef as React.RefObject<HTMLDivElement>}>
            {egyptSectors.map((sector, si) => (
              <SectorBlock key={si} sector={sector} />
            ))}
          </div>
        </div>
      </section>

    </main>
  )
}
